import React, { useEffect, useMemo, useState } from "react";
import NotificationSlider from "./NotificationSlider";
import { getUserPoints, UserPointData } from "../../api/points";

type UserInfo = {
    avatar?: string;
    fullname?: string;
};

const FALLBACK_AVATAR =
    "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=";

export default function DashboardTopSection() {
    const [pointData, setPointData] = useState<UserPointData | null>(null);

    useEffect(() => {
        const loadPoints = async () => {
            const response = await getUserPoints();
            if (response?.status && response.data) {
                setPointData(response.data);
            }
        };

        loadPoints();
    }, []);

    const userInfo = useMemo<UserInfo | null>(() => {
        if (typeof window === "undefined") return null;
        try {
            const raw = localStorage.getItem("user");
            if (!raw) return null;
            return JSON.parse(raw);
        } catch {
            return null;
        }
    }, []);

    const fullName = userInfo?.fullname?.trim() || "Khách hàng";
    const nameParts = fullName.split(/\s+/).filter(Boolean);
    const firstInitial = nameParts[0]?.charAt(0) || "?";
    const lastInitial = nameParts[nameParts.length - 1]?.charAt(0) || firstInitial;
    const userInitials = `${firstInitial}${lastInitial}`.toUpperCase();
    const avatarSrc = userInfo?.avatar || FALLBACK_AVATAR;
    const pointBalance = Number(pointData?.point_balance || 0);
    const percent = Math.max(0, Math.min(100, Number(pointData?.percent || 0)));
    const currentLevel = (pointData?.level || "Silver").trim();
    const nextLevel = (pointData?.next_level || "").trim();

    return (
        <div className="grid xl:!grid-cols-6 !grid-cols-4 gap-4">
            {/* ================= LEFT ================= */}
            <div className="xl:!col-span-2 !col-span-4">
                <div
                    id="BLOCK_CUSTOMER_INFO"
                    event-block="BLOCK_CUSTOMER_INFO"
                    className="h-full"
                >
                    <div
                        className="w-full h-full p-2 px-3 relative min-w-[300px] rounded-[10px] overflow-hidden bg-center bg-no-repeat bg-cover"
                        style={{
                            background: "url('https://thuocsi.vn/images/bg-silver-1.png') 0% 0% / 100% 100%",
                        }}
                    >
                        <div className="flex flex-col justify-around gap-2 relative h-full">
                            {/* AVATAR */}
                            <div className="flex items-center gap-2">
                                <div className="group relative rounded-full min-w-[86px] w-[86px] h-[86px] overflow-hidden">
                                    <img
                                        src={avatarSrc}
                                        alt="Avatar"
                                        loading="lazy"
                                        decoding="async"
                                        className="absolute inset-0 w-full h-full object-cover"
                                        onError={(e) => {
                                            e.currentTarget.onerror = null;
                                            e.currentTarget.src = FALLBACK_AVATAR;
                                        }}
                                    />

                                    <div className="opacity-0 group-hover:opacity-100 transition-all absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
                                        <label
                                            htmlFor="avatar-upload"
                                            className="cursor-pointer h-full w-full flex items-center justify-center"
                                        >
                                            <svg
                                                className="MuiSvgIcon-root !text-white"
                                                viewBox="0 0 24 24"
                                            >
                                                <path d="M3 4V1h2v3h3v2H5v3H3V6H0V4h3zm3 6V7h3V4h7l1.83 2H21c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H5c-1.1 0-2-.9-2-2V10h3zm7 9c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5z" />
                                            </svg>
                                        </label>
                                        <input
                                            id="avatar-upload"
                                            type="file"
                                            accept="image/png, image/jpeg"
                                            className="hidden"
                                        />
                                    </div>
                                    {
                                        avatarSrc === FALLBACK_AVATAR && (
                                            <div className="absolute inset-0 flex items-center justify-center rounded-full bg-gradient text-white font-semibold text-2xl">
                                                {userInitials}
                                            </div>
                                        )
                                    }

                                </div>

                                <div className="flex flex-col gap-2">
                                    <p className="text-[16px] leading-[20px] font-semibold line-clamp-2">
                                        {fullName}
                                    </p>
                                </div>
                            </div>

                            {/* LEVEL */}
                            <div
                                className="flex flex-col gap-2 bg-white/30 p-2 rounded-[8px] border border-white"
                                style={{
                                    background: "url('https://thuocsi.vn/images/bg-silver-2.png') 0% 0% / 100% 100%",
                                }}
                            >
                                <div className="flex justify-between items-center">
                                    <img
                                        src={pointData?.level_avatar}
                                        className="h-[32px]"
                                        loading="lazy"
                                        decoding="async"
                                    />
                                    <p>
                                        <strong>{pointBalance.toLocaleString("vi-VN")}</strong> điểm
                                    </p>
                                </div>

                                <div className="w-full bg-white rounded-full overflow-hidden">
                                    <div
                                        className="bg-gradient h-1 rounded-full"
                                        style={{ width: `${percent}%` }}
                                    />
                                </div>

                                <div className="flex gap-1 items-center">
                                    <p>
                                        Hạng hiện tại: <span className="text-[var(--primary-color)] font-semibold">{currentLevel}</span>
                                        {nextLevel ? ` - Tiếp theo: ${nextLevel} (${percent}%)` : ""}
                                    </p>
                                    {
                                        nextLevel && (
                                            <a href="/users/loyalty_points?type=1" target="_blank" rel="noreferrer">
                                                <img
                                                    src={pointData?.next_level === "" ? pointData?.level_avatar : pointData?.next_level_avatar}
                                                    className="w-[100px]"
                                                    loading="lazy"
                                                    decoding="async"
                                                />
                                            </a>
                                        )
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ================= RIGHT ================= */}
            <div className="col-span-4">
                <NotificationSlider />
            </div>
        </div>
    );
}